import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./Room1.css";
import Footer from "../Components/Footer";
import NavBar from "../Components/NavBar";
import SeoMeta from "../Components/SeoMeta";

function Room1() {
  const [roomData, setRoomData] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const images = [
    "https://nimantrana-media.s3.ap-south-1.amazonaws.com/cityView1.jpg",
    "https://nimantrana-media.s3.ap-south-1.amazonaws.com/cityView2.jpg",
    "https://nimantrana-media.s3.ap-south-1.amazonaws.com/cityView3.jpg",
    "https://nimantrana-media.s3.ap-south-1.amazonaws.com/cityView4.jpg",
  ];

  const location = useLocation();
  const canonicalUrl = `https://www.nimantrana.co.in${location.pathname || '/suiteroomcityview'}`;
  const metaTitle = roomData?.roomName ? `${roomData.roomName} | Nimantrana Suites` : 'City View Suite in Noida | Nimantrana';
  const metaDescription = roomData?.roomDescription || 'Book the city view suite at Nimantrana for skyline views, premium amenities, and bespoke hospitality.';
  const roomSchema = roomData ? {
    "@context": "https://schema.org",
    "@type": "HotelRoom",
    "name": roomData.roomName,
    "description": roomData.roomDescription,
    "image": images,
    "offers": {
      "@type": "Offer",
      "price": roomData.discountedPrice || roomData.roomPrice,
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
      "url": canonicalUrl
    },
    "occupancy": {
      "@type": "QuantitativeValue",
      "value": 2,
      "unitCode": "C62"
    },
    "bed": "King bed",
    "url": canonicalUrl
  } : null;

  useEffect(() => {
    fetchRoomData();
  }, []);

  const fetchRoomData = async () => {
    try {
      const response = await fetch(
        "https://backend.nimantrana.co.in/api/rooms/6880f8988c99a461a47469c4"
      );
      const result = await response.json();
      if (result.success) {
        setRoomData(result.data);
      }
    } catch (error) {
      console.error("Error fetching room data:", error);
    } finally {
      setLoading(false);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentImageIndex(index);
  };

  if (loading) {
    return (
      <>
        <SeoMeta
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalUrl}
          image={images[0]}
          structuredData={roomSchema}
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </>
    );
  }

  if (!roomData) {
    return (
      <>
        <SeoMeta
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalUrl}
          image={images[0]}
        />
        <div className="error-container">
          <h2>Room not found</h2>
        </div>
      </>
    );
  }

  const discountPercentage = Math.round(
    ((roomData.roomPrice - roomData.discountedPrice) / roomData.roomPrice) * 100
  );

  return (
    <>
      <div className="room-page">
        <SeoMeta
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalUrl}
          image={images[0]}
          structuredData={roomSchema}
        />
        <NavBar />

        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Experience Luxury Redefined</h1>
              <p style={{ color: "#DCB15D" }}>
                Step into unparalleled elegance with premium comfort and
                stunning city views
              </p>
            </div>
          </div>
        </div>

        <div className="room-container">
          {/* Image Gallery Section */}
          <div className="image-gallery-section">
            <div className="main-image-container">
              <img
                src={images[currentImageIndex]}
                alt={`${roomData.roomName} - Image ${currentImageIndex + 1}`}
                className="main-image"
              />
              <button
                style={{ color: "#DCB15D" }}
                className="nav-btn prev-btn"
                onClick={prevImage}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                style={{ color: "#DCB15D" }}
                className="nav-btn next-btn"
                onClick={nextImage}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className="image-indicators">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator ${
                      index === currentImageIndex ? "active" : ""
                    }`}
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </div>
            </div>

            <div className="thumbnail-container">
              {images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className={`thumbnail ${
                    index === currentImageIndex ? "active" : ""
                  }`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </div>

          {/* Room Details Section */}
          <div className="room-details-section">
            <div className="room-header">
              <h1 style={{ color: "#DCB15D" }} className="room-name">
                {roomData.roomName}
              </h1>
              <p className="hotel-name">{roomData.hotelName}</p>

              <div className="price-section">
                <div className="price-display">
                  <span className="original-price">
                    ₹{roomData.roomPrice.toLocaleString()}
                  </span>
                  <div className="current-price-container">
                    <span className="current-price">
                      ₹{roomData.discountedPrice.toLocaleString()}
                    </span>
                    <span className="price-period">per night</span>
                  </div>
                </div>
                <div className="discount-tag">Save {discountPercentage}%</div>
              </div>
            </div>

            <div className="room-description">
              <h2>About This Suite</h2>
              <p>{roomData.roomDescription}</p>
            </div>

            <div className="room-features">
              <h2>Suite Features</h2>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="3"
                        y="6"
                        width="18"
                        height="12"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M7 6V4C7 3.45 7.45 3 8 3H16C16.55 3 17 3.45 17 4V6"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <div className="feature-content">
                    <h3>Luxury Bedding</h3>
                    <p>King-sized bed with premium linens</p>
                  </div>
                </div>

                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 9L12 2L21 9V20C21 20.55 20.55 21 20 21H4C3.45 21 3 20.55 3 20V9Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <polyline
                        points="9,22 9,12 15,12 15,22"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <div className="feature-content">
                    <h3>Panoramic Views</h3>
                    <p>Stunning city skyline views</p>
                  </div>
                </div>

                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M1 6V8C1 9.1 1.9 10 3 10H4C5.1 10 6 9.1 6 8V6C6 4.9 5.1 4 4 4H3C1.9 4 1 4.9 1 6Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M5 10V16C5 17.1 5.9 18 7 18H17C18.1 18 19 17.1 19 16V10"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M19 4H20C21.1 4 22 4.9 22 6V8C22 9.1 21.1 10 20 10H19C17.9 10 17 9.1 17 8V6C17 4.9 17.9 4 19 4Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <div className="feature-content">
                    <h3>Separate Living</h3>
                    <p>Spacious living area</p>
                  </div>
                </div>

                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <div className="feature-content">
                    <h3>Premium Service</h3>
                    <p>24/7 concierge service</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="booking-section">
              <div className="booking-details">
                <div className="detail-item">
                  <span className="detail-label">Check-in</span>
                  <span className="detail-value">1:00 PM</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Check-out</span>
                  <span className="detail-value">11:00 AM</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Max Guests</span>
                  <span className="detail-value">2 Adults</span>
                </div>
              </div>

              <div className="action-buttons">
                <button className="btn-primary">
                  Reserve Suite
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default Room1;
